<div class="container">
  <h1>Source Commission Report</h1>

  <!-- Source selector: dropdown for Admin, fixed label for Agent -->
  <div class="source-selector">
    <label for="sourceSelect">Source:</label>
    <ng-container *ngIf="isAdmin">
      <select id="sourceSelect" [(ngModel)]="selectedSource" (ngModelChange)="onSourceChange()">
        <option value="">— Select a Source —</option>
        <option *ngFor="let u of agentUsers" [value]="u.id">{{ u.firstName }} {{ u.lastName }} ({{ getRoleLabel(u.role) }})</option>
      </select>
      <span *ngIf="selectedSource && selectedAgentSalary != null" class="agent-salary">
        {{ selectedAgentSalary | currency:'USD':'symbol':'1.0-0' }}
      </span>
    </ng-container>
    <ng-container *ngIf="!isAdmin">
      <span class="agent-name-label">{{ currentUserFullName }}</span>
      <span *ngIf="selectedAgentSalary != null" class="agent-salary">
        {{ selectedAgentSalary | currency:'USD':'symbol':'1.0-0' }}
      </span>
    </ng-container>
  </div>

  <div *ngIf="loading" class="loading">Loading data…</div>

  <ng-container *ngIf="!loading && selectedSource">

    <!-- Commission summary cards -->
    <div class="summary">
      <div *ngIf="!isScouting" class="summary-card current">
        <div class="summary-label">Current Commission</div>
        <div class="summary-value">{{ currentCommission | currency:'USD':'symbol':'1.0-0' }}</div>
      </div>
      <div *ngIf="!isScouting" class="summary-card future">
        <div class="summary-label">Future Commission</div>
        <div class="summary-value">{{ futureCommission | currency:'USD':'symbol':'1.0-0' }}</div>
      </div>
      <div *ngIf="showBonusData" class="summary-card bonus">
        <div class="summary-label">Total Bonus Amount</div>
        <div class="summary-value">{{ bonusAmount | currency:'USD':'symbol':'1.0-0' }}</div>
      </div>
    </div>

    <!-- Commission chart + bonus table -->
    <div *ngIf="chartData || bonusRows.length > 0" class="chart-and-bonus">
      <div *ngIf="chartData" class="chart-wrapper">
        <canvas baseChart [data]="chartData" [options]="chartOptions" [type]="'line'" [plugins]="chartPlugins" (chartClick)="onChartClick($event)"></canvas>
      </div>
      <div *ngIf="bonusRows.length > 0" class="bonus-table-wrapper">
        <div class="bonus-table-title">Bonus Schedule</div>
        <table class="bonus-table">
          <thead>
            <tr>
              <th>Date</th>
              <th *ngIf="isScouting">Creator</th>
              <th class="right">Bonus</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of bonusRows; let i = index"
                [class.bonus-projected]="row.isProjected"
                [class.bonus-row-active]="activeBonusRowIndex === i"
                (click)="!isScouting && onBonusRowClick(row, i)"
                [style.cursor]="isScouting ? 'default' : 'pointer'">
              <td>{{ row.date }}<span *ngIf="!isScouting && !row.isProjected" class="actual-tag">actual</span><span *ngIf="row.isProjected" class="proj-tag">proj</span></td>
              <td *ngIf="isScouting" class="roster-name-cell">{{ row.rosterName || '' }}</td>
              <td class="right">{{ row.amount | currency:'USD':'symbol':'1.0-0' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Color legend (not shown for Scouting since all rows are uniform) -->
    <div *ngIf="!isScouting && rows.length > 0" class="legend">
      <div class="legend-item"><div class="legend-swatch yellow"></div><span>No Invoice Yet</span></div>
      <div class="legend-item"><div class="legend-swatch pending"></div><span>Invoiced - Not Paid</span></div>
      <div class="legend-item"><div class="legend-swatch projected"></div><span>Uninvoiced Remainder</span></div>
      <div class="legend-item"><div class="legend-swatch green"></div><span>Invoiced - Paid</span></div>
    </div>

    <!-- Full table for Agent / Casting roles (12 columns) -->
    <div *ngIf="!isScouting && rows.length > 0" class="report-table">
      <div *ngIf="chartPointFilter" class="filter-bar">
        <span *ngIf="chartFilterDate">{{ chartFilterDate }} &nbsp;·&nbsp; </span>Showing {{ visibleRows.length }} of {{ rows.length }} rows &nbsp;
        <a href="javascript:void(0)" (click)="clearChartFilter()">Clear Filter</a>
      </div>

      <!-- Header row -->
      <div class="header">Campaign</div>
      <div class="header">Created</div>
      <div class="header">Creator</div>
      <div class="header right">Rate</div>
      <div class="header right">Comm %</div>
      <div class="header">Invoice #</div>
      <div class="header">Due Date</div>
      <div class="header right">Invoice Amount</div>
      <div class="header">Paid Date</div>
      <div class="header right">Actual Pay</div>
      <div class="header right">Creator Pay</div>
      <div class="header right">Expense</div>
      <div class="header right">VT Kept</div>
      <div class="header right">Commission</div>

      <!-- Data rows -->
      <ng-container *ngFor="let row of visibleRows">

        <!-- Campaign name linking to the campaign page -->
        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <a [routerLink]="['/agent/campaigns', row.campaignId]">{{ row.campaignName }}</a>
        </div>

        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.campaignCreatedDate }}
        </div>

        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.rosterName }}
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.campaignRate | currency:'USD':'symbol':'1.0-0' }}
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.campaignCommission }}%
        </div>

        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.invoiceNumber }}
        </div>

        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.invoiceDueDate }}
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.invoiceAmount | currency:'USD':'symbol':'1.0-0' }}
        </div>

        <div class="cell" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          {{ row.paidDate ?? '' }}
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <ng-container *ngIf="row.actualPayAmount != null">
            {{ row.actualPayAmount | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="row.actualPayAmount == null">—</ng-container>
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <ng-container *ngIf="row.creatorPayAmount != null">
            {{ row.creatorPayAmount | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="row.creatorPayAmount == null">—</ng-container>
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <ng-container *ngIf="row.expenseAmount">
            {{ row.expenseAmount | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="!row.expenseAmount">—</ng-container>
        </div>

        <div class="cell right" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <ng-container *ngIf="row.vtKeptExpense">
            {{ row.vtKeptExpense | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="!row.vtKeptExpense">—</ng-container>
        </div>

        <div class="cell right bold" [class.green]="row.isPaid" [class.yellow]="row.isNoInvoice" [class.pending]="!row.isPaid && !row.isNoInvoice && !row.isProjected" [class.projected]="row.isProjected">
          <ng-container *ngIf="row.isPaid">
            {{ row.commissionEarned | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="!row.isPaid && !row.isNoInvoice">
            {{ (row.invoiceAmount - (row.expenseAmount ?? 0) - (row.commissionAmount ?? 0)) * row.campaignCommission / 100 + (row.commissionAmount ?? 0) | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
          <ng-container *ngIf="row.isNoInvoice">
            {{ row.campaignRate * row.campaignCommission / 100 | currency:'USD':'symbol':'1.0-0' }}
          </ng-container>
        </div>

      </ng-container>

      <!-- Total row -->
      <div class="cell total-label" style="grid-column: span 12; text-align: right;">Total Commission</div>
      <div class="cell total-value right bold">{{ totalCommission | currency:'USD':'symbol':'1.0-0' }}</div>

    </div>

    <div *ngIf="rows.length === 0" class="no-data">
      <ng-container *ngIf="isAdmin">No campaigns found for source "{{ resolveUserId(selectedSource) }}".</ng-container>
      <ng-container *ngIf="!isAdmin">No Campaigns for this Agent.</ng-container>
    </div>

  </ng-container>
</div>
